import type { RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'

/**
 * The candidate-app module's routes (wp/candidate-app §3; manifest rows of WP-17 — paths and names from the manifest,
 * D-15; the legacy paths redirect here through `src/app/router/legacy-redirects.ts`): the stage of the applications
 * list is the `?stage=` query, the detail takes an **application** id, the CV editor is `/ca/resume/edit`.
 *
 * `meta.software: 'ca'` on every route: the software guard (D-21) sends a Client or Staff member to the not-authorized
 * page — legacy guarded `/ca/resume` only (`roles: ['Freelancer']`) and let them reach broken pages (NEW-19, OPEN-2).
 * No ACL resource (legacy had none; every Freelancer may use these pages). `/ca/dashboard` is WP-18's.
 */
const applicationsCrumb = { text: keys.t('common.nav.applications'), to: '/ca/applications' }
const ApplicationsView = () => import('./views/ApplicationsView.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/ca/applications',
    name: 'ca-applications',
    component: ApplicationsView,
    meta: {
      software: 'ca',
      pageTitle: keys.t('common.nav.applications'),
      breadcrumb: [{ text: keys.t('common.nav.applications'), active: true }],
    },
  },
  {
    // Legacy `ca-applications-redirect` (§4.2): the nav entry's name. The bare `/ca/applications` is served by the
    // record above, which opens the first stage with applications; this name lands there with the query and hash kept.
    path: '/ca/applications',
    name: 'ca-applications-redirect',
    redirect: (to) => ({ name: 'ca-applications', query: to.query, hash: to.hash }),
  },
  {
    path: '/ca/applications/:id(\\d+)',
    name: 'ca-application',
    component: () => import('./views/ApplicationView.vue'),
    meta: {
      software: 'ca',
      // Legacy `navActiveLink: 'ca-applications-redirect'`: the nav's Applications entry, which resolves to this name.
      navActiveLink: 'ca-applications',
      pageTitle: keys.t('candidateApp.application.title.page'),
      breadcrumb: [applicationsCrumb, { text: keys.t('candidateApp.application.title.page'), active: true }],
    },
  },
  {
    path: '/ca/resume',
    name: 'ca-resume',
    component: () => import('./views/ResumeView.vue'),
    meta: {
      software: 'ca',
      pageTitle: keys.t('candidateApp.resume.title.page'),
      breadcrumb: [{ text: keys.t('candidateApp.resume.title.page'), active: true }],
    },
  },
  {
    path: '/ca/resume/edit',
    name: 'ca-settings',
    component: () => import('./views/ResumeEditView.vue'),
    meta: {
      software: 'ca',
      pageTitle: keys.t('common.nav.settings'),
      breadcrumb: [{ text: keys.t('common.nav.settings'), active: true }],
    },
  },
]

export default routes
